import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, ExternalLink } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { format, parseISO } from 'date-fns';
import { notificationsApi } from '../../lib/api';
import { pausePollingOnError } from '../../lib/queryHelpers';
import { useAuthStore, useNotificationStore } from '../../store';
import type { Notification } from '../../types';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setNotifications, unreadCount, markAsRead, markAllAsRead } = useNotificationStore();
  // Gate on both Auth0 session AND the local store token so we don't poll
  // during the brief boot window between Auth0 rehydration and useCurrentUser()
  // writing the Bearer token into Zustand.
  const { isAuthenticated } = useAuth0();
  const token = useAuthStore(s => s.token);
  const canFetch = isAuthenticated && !!token;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    enabled: canFetch,
    queryFn: async () => {
      const res = await notificationsApi.getAll();
      const notifications = res.data.data as Notification[];
      setNotifications(notifications);
      return notifications;
    },
    // Pause polling while failing (see pausePollingOnError). This observer shares
    // the ['notifications'] query with useNotifications() — both must use the
    // guard, else the shared query keeps polling at this 30s interval on error.
    refetchInterval: pausePollingOnError(30000),
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: (_, id) => {
      markAsRead(id);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      markAllAsRead();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const notifications = data || [];
  const EVENT_ICONS: Record<string, string> = {
    task_assigned: '📋',
    task_overdue: '🔴',
    sla_breached: '⚡',
    import_complete: '✅',
    import_error: '⚠️',
    account_locked: '🔒',
    security_event: '🛡️',
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  className="text-xs text-[#C94B1F] hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {/* N2 — isLoading gate: without it, a still-loading fetch briefly shows "No
                notifications" (the false-empty-state class) before the real list lands. */}
            {isLoading ? (
              <div className="py-6 px-4 space-y-2" aria-busy="true">
                <span className="sr-only" role="status">Loading notifications…</span>
                {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications
              </div>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <div
                  key={n.notification_id}
                  onClick={() => {
                    if (!n.is_read) markRead.mutate(n.notification_id);
                    // N1 — was `window.location.hash = n.link`, a no-op under BrowserRouter.
                    // Stored links are plain paths, BUT task links use the legacy `/tasks/<id>`
                    // path-segment shape, while the deep-link contract is `/tasks?task=<id>`.
                    // App.tsx now also redirects `/tasks/:id` there (TaskDeepLinkRedirect, for
                    // email links); normalizing before navigating still saves that hop (gate-caught).
                    if (n.link) {
                      const link = n.link.replace(/^\/tasks\/([0-9a-f-]{8,})$/i, '/tasks?task=$1');
                      navigate(link);
                      setOpen(false);
                    }
                  }}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${!n.is_read ? 'bg-blue-50/30' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-base flex-shrink-0 mt-0.5">
                      {EVENT_ICONS[n.event_type] || '🔔'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-xs font-semibold truncate ${!n.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {n.title}
                        </p>
                        {!n.is_read && (
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {format(parseISO(n.created_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                    {n.link && (
                      <ExternalLink className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">{notifications.length} total notification{notifications.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
